import {ActionType} from "typesafe-actions";
import * as actions from "./actions";
import {SceneryType} from "../settings/SceneryType";
import {BonusType} from "../player/types";
import {Language} from "../settings/Language";

export interface IGameState {
    readonly levelInProgress: number;
    readonly currentTileId: number;
    readonly levelScenery: SceneryType;
    readonly categories: Record<number, ICategory>;
    readonly categoriesFetchTime: number;
    readonly questions: Record<number, IAggregatedQuestion>;
    readonly unusedQuestions: number[];
    readonly unusedQuestionsFetchLanguage: Language;
    readonly answers: Record<number, IQuestionAnswer>;
    readonly authors: Record<string, IQuestionAuthor>;
    readonly levelMaps: Record<number, ILevelMap>;
    readonly levelMapProgress: Record<number, ILevelMapProgress>;
    readonly levelMapTileProgress: Record<number, ILevelMapTileProgress>;
    readonly dailyStreakLastTimeProcessed: number;
    readonly dailyStreak: number;
}

export type GameActions = ActionType<typeof actions>;


//-----------

export interface ILevelGameState {
    readonly questions: Record<number, IAggregatedQuestion>;
    readonly answers: Record<number, IQuestionAnswer>;
    readonly authors: Record<number, IQuestionAuthor>;
    readonly levelMaps: Record<number, ILevelMap>;
    readonly unusedQuestions: number[];
    readonly languageChanged?: boolean;
    readonly unusedQuestionsFetchLanguage?: Language;
}

export interface ILevelMapStats {
    readonly id: number;
    readonly answeredQuestions: number;
    readonly totalQuestions: number;
    readonly totalScore: number;
    readonly completionPercent: number;
    readonly rightAnswersPercent: number;
    readonly completeLevelTime: number;
    readonly stars: number;
}

export interface ILevelMapProgress extends ILevelMapStats {
    readonly id: number;
    readonly startLevelTime: number;
    readonly appliedHints: BonusType[];
    readonly activeHints: BonusType[];
    readonly isExitFound: boolean;
    readonly allTilesAreRevealed: boolean;
    readonly isCompleted: boolean;
    readonly answeredTileCount: number;
    readonly scoreMultiplier: number;
}

export interface ILevelMapTileProgress {
    readonly id: number;
    readonly overriddenQuestions?: Record<number, number>;
    readonly isRevealed: boolean;
    readonly isAnswered?: boolean;
    readonly isRight?: boolean;
    readonly score?: number;
    readonly additionalScore?: number;
    readonly bonus?: BonusType;
    readonly timestamp?: number;
}

export interface ILevelMap {
    readonly levelId: number;
    readonly exitTileId: number;
    readonly tileCount: number;
    readonly tiles: ILevelMapAggregatedTile[][];
    readonly decals?: ILevelMapDecal[];
}

export interface ILevelMapDecal {
    readonly type: DecalType;
    readonly index: number;
    readonly coords: IPoint;
}

export interface ILevelMapAggregatedTile {
    readonly id: number;
    readonly levelId: number;
    readonly tileType: TileType;
    readonly isRevealed: boolean;
    readonly isBonusQuestion: boolean;
    readonly questionCount: number;
    readonly questions: number[];
    readonly coords: IPoint;
}

export interface IPoint {
    readonly x: number;
    readonly y: number;
}

export enum DecalType {
    None = 0,
    Big = 10,
    Small = 20
}

export enum TileType {
    Blocked = 0,
    SimpleQuestion = 1,
    Open = 2,
    Exit = 3,
    CardsGame = 4,
    ChestGame = 5
}

export interface IQuestion {
    readonly id: number;
    readonly difficulty: QuestionDifficulty;
    readonly title: string;
    readonly description: string;
    readonly reputationPoints: number;
    readonly categoryId: number;
    readonly answers: IQuestionAnswer[];
    readonly answerStats: IAnswerStats;
    readonly author: IQuestionAuthor | null | undefined;
    readonly imageUrl: string;
    readonly thumbnailImageUrl: string;
    readonly smallImageUrl: string;
    readonly dislikesToLikesPercent: number;
    readonly feedbacksToTotalAnswersPercent: number;
    readonly source: IContentSource | null | undefined;
    readonly imageSource: IContentSource | null | undefined;
}

export interface IAggregatedQuestion extends Pick<IQuestion, Exclude<keyof IQuestion, "answers" | "author">> {
    readonly answers: number[];
    readonly author?: string;
}

export interface IQuestionAnswer {
    readonly id: number;
    readonly questionId: number;
    readonly text: string;
    readonly isRight: boolean;
    readonly sortOrder: number;
}

export interface IAnswerStats {
    readonly questionId: number;
    readonly stats: IAnswerStatsItem[];
    readonly totalAnswerCount: number;
    readonly easiness: number;
}

export interface IAnswerStatsItem {
    readonly answerId: number;
    readonly count: number;
}

export interface IQuestionAuthor {
    readonly id: string;
    readonly name: string;
    readonly imageUrl: string | null | undefined;
    readonly email: string | null | undefined;
}

export interface IContentSource {
    readonly show: boolean;
    readonly url: string;
    readonly showUrl: boolean;
    readonly title: string;
    readonly isMediaSource: boolean;
}

export interface ICategory {
    readonly id: number;
    readonly title: string;
    readonly name: string;
    readonly backgroundColor: string;
    readonly imageUrl: string;
}

export enum QuestionDifficulty {
    Normal = 0,
    Easy = 1,
    Hard = 2,
    Extreme = 3,
    Unknown = 50
}